import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  MIXPANEL_WEB_SOURCE,
  sendMixpanelEvent
} from '@core/analytics/mixpanel';
import { store } from '@core/store';
import { getLocalStorageItem } from '@utils/storage';

import type { ButtonAction, ComponentInstance } from '../types';
import { ComponentType } from '../types';

type UserSliceForDynamicAnalytics = {
  currentUser?: { name?: string; role_name?: string };
  currentOrg?: { name?: string; role_name?: string } | null;
};

/** Property set for dynamic-component Mixpanel events (same `source` as other web events). */
export type DynamicComponentMixpanelPayload = {
  user_name: string;
  user_role: string;
  org_name: string;
  branch_name: string;
  text: string;
  action_type: string;
  action_target: string;
  source: string;
  notification_type: ComponentType;
  notification_name: string;
};

const buildBasePayload = (
  componentType: ComponentType,
  instance: ComponentInstance
): Pick<
  DynamicComponentMixpanelPayload,
  | 'user_name'
  | 'user_role'
  | 'org_name'
  | 'branch_name'
  | 'source'
  | 'notification_name'
  | 'notification_type'
> => {
  const state = store.getState() as { user?: UserSliceForDynamicAnalytics };
  const currentUser = state.user?.currentUser ?? {};
  const org = state.user?.currentOrg;
  const roleFromOrg = typeof org?.role_name === 'string' ? org.role_name : '';
  const roleFromUser =
    typeof currentUser.role_name === 'string' ? currentUser.role_name : '';
  return {
    user_name: typeof currentUser.name === 'string' ? currentUser.name : '',
    user_role: roleFromOrg || roleFromUser,
    org_name: typeof org?.name === 'string' ? org.name : '',
    branch_name: getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_NAME) ?? '',
    source: MIXPANEL_WEB_SOURCE,
    notification_name: instance.name,
    notification_type: componentType
  };
};

const actionTargetFromPayload = (action: ButtonAction['action']): string => {
  if ('target' in action && typeof action.target === 'string') {
    return action.target;
  }
  return '';
};

export const trackDynamicComponentViewed = (
  instance: ComponentInstance,
  componentType: ComponentType
): void => {
  sendMixpanelEvent(Event.DYNAMIC_NOTIFICATION_VIEWED, {
    ...buildBasePayload(componentType, instance),
    action_desc: instance.data.title ?? '',
    action_type: '',
    action_target: ''
  });
};

export const trackDynamicComponentCta = (
  button: ButtonAction,
  instance: ComponentInstance,
  componentType: ComponentType
): void => {
  sendMixpanelEvent(Event.DYNAMIC_NOTIFICATION_CTA_CLICKED, {
    ...buildBasePayload(componentType, instance),
    action_desc: button.text ?? '',
    action_type: button.action.type,
    action_target: actionTargetFromPayload(button.action)
  });
};

export const trackDynamicComponentDismissed = (
  instance: ComponentInstance,
  componentType: ComponentType,
  action_desc?: string
): void => {
  sendMixpanelEvent(Event.DYNAMIC_NOTIFICATION_CTA_CLICKED, {
    ...buildBasePayload(componentType, instance),
    action_desc,
    action_type: 'dismiss',
    action_target: ''
  });
};
